import React, { useEffect, useState } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { closeModal } from '../../../store/slices/modalSlice';
import { modalExtra } from '../../../selectors/selectors';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import { useMakeConsumerTransactionMutation } from '../../consumer/consumer.service';
import { convertTransactionAmount } from '../../../utils/convertTransactionAmount';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import cls from './MakeConsumerTransaction.module.scss';

const MakeConsumerTransaction = () => {
  const extra = useAppSelector(modalExtra);
  const dispatch = useAppDispatch();
  const [makeTransactionByAdmin] = useMakeConsumerTransactionMutation();
  const [disabled, setDisabled] = useState<boolean>(false);
  const resetHandler = extra?.handler as () => void;
  const closeHandler = (): void => {
    dispatch(closeModal());
  };
  const validationSchema = yup
    .object()
    .strict()
    .shape({
      amount: yup.number().min(0.01, 'Minimum field value 0.01').required('This field is required'),
    });

  const formik = useFormik({
    initialValues: { amount: 0, type: 'INCOMING' },
    validationSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      const { amount, type } = values;
      try {
        if (amount.toString().includes('.') && amount.toString().split('.')[1].length > 2) {
          setDisabled(true);
          formik.setErrors({ amount: 'Invalid amount format' });
          toast.error('Invalid amount format');
        } else {
          const finallyAmount = convertTransactionAmount(amount);
          setDisabled(false);
          try {
            await makeTransactionByAdmin({
              id: extra?.id as string,
              amount: finallyAmount,
              typeOperation: type,
            }).unwrap();
            resetHandler();
            toast.success('Transaction completed successfully');
            dispatch(closeModal());
          } catch (error) {
            if (isFetchBaseQueryError(error)) {
              if (error.status === 400) {
                toast.error('Please try again later');
              } else if (error.status === 403) {
                toast.error('Forbidden');
              } else if (error.status === 404) {
                toast.error('User not found');
              }
            }
          } finally {
            setDisabled(false);
          }
        }
      } catch (error) {
        console.log(error);
        toast.error('Oops. Something went wrong');
      } finally {
        setDisabled(false);
      }
    },
  });
  useEffect(() => {
    if (formik.values.amount === 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [formik.values.amount]);
  return (
    <>
      <Modal.Header className={cls.ModalHeader} closeButton>
        <h3 className="title-3" data-testid="modal-title">
          {extra?.title}
        </h3>
      </Modal.Header>
      <Modal.Body className={cls.ModalBody}>
        <div data-testid="text-part">
          <p>
            {extra?.supportText} <strong>{extra?.textInformation}</strong>
          </p>
        </div>
        <Form onSubmit={formik.handleSubmit} className={cls.Form}>
          <Form.Group className="position-relative">
            <Form.Label htmlFor="amount">Enter amount</Form.Label>
            <Form.Control
              type="number"
              value={formik.values.amount}
              placeholder="Enter amount"
              onChange={formik.handleChange}
              name="amount"
              id="amount"
              isInvalid={!!formik.errors.amount}
            />
            <Form.Control.Feedback type="invalid" tooltip>
              {formik.errors.amount}
            </Form.Control.Feedback>
          </Form.Group>
          <div>
            <label htmlFor="type">Select operation type</label>
            <Form.Select onChange={(event) => formik.handleChange(event)} name="type" id="type">
              <option disabled>Select transaction type.</option>
              <option value="INCOMING" id="INCOMING">
                Add funds
              </option>
              <option value="OUTGOING" id="OUTGOING">
                Withdraw funds
              </option>
            </Form.Select>
          </div>
          <div className={cls.Buttons}>
            <Button type="submit" className={cls.Button} disabled={disabled}>
              {extra?.actionButtonText}
            </Button>
            <Button onClick={closeHandler} className={cls.CancelButton}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};

export default MakeConsumerTransaction;
